import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, Phone, ChevronLeft, ShieldCheck, Truck, Package, FileText } from "lucide-react";
import EnquiryDialog from "@/components/EnquiryDialog";
import Seo from "@/components/Seo";
import SchemaLd from "@/components/SchemaLd";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { useLabels } from "@/lib/labels";
import ProductServices from "@/components/cms/ProductServices";

const plainText = (h) => String(h || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

const cleanSpecs = (html) => {
  if (!html) return "";
  let s = String(html).replace(/^\s*<h2[^>]*>[\s\S]*?<\/h2>/i, "");
  s = s.replace(/\\n/g, "\n");                       // literal "\n" -> real newline
  s = s.replace(/-?\s*See more at:[^\n<]*/gi, "");   // strip competitor "see more" links
  s = s.replace(/[ \t]*\n[ \t]*/g, "\n");
  s = s.replace(/\n{2,}/g, "\n");                    // collapse blank lines
  s = s.replace(/\n/g, "<br>");
  s = s.replace(/(<br>\s*){2,}/g, "<br>");           // collapse stacked breaks
  return DOMPurify.sanitize(s);
};

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const L = useLabels();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [enquiry, setEnquiry] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${id}`).then((r) => {
      // 301-style redirect: old /product/<uuid> links resolve to the friendly slug URL.
      if (r.data?.slug && r.data.slug !== id) {
        navigate(`/product/${r.data.slug}`, { replace: true });
        return;
      }
      setP(r.data);
    }).catch(() => {
      api.get(`/resolve-path`, { params: { path: `/product/${id}` } }).then(({ data }) => {
        const to = data.kind === "redirect" ? data.to : data.kind === "product" ? `/product/${data.slug}` : null;
        if (to && to !== `/product/${id}`) navigate(to, { replace: true });
        else { setP(false); api.post("/url-repair/report-404", { path: `/product/${id}` }).catch(() => {}); }
      }).catch(() => setP(false));
    });
  }, [id, navigate]);

  if (p === false) return <div className="p-20 text-center" data-testid="pdp-not-found">Product not found. <a href="/" className="text-accent underline">Browse the catalogue</a></div>;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      {p && (
        <Seo
          title={p.name}
          description={p.meta_effective || `${p.name} (SKU ${p.sku}) — available from ACP Online, New Zealand.`}
          image={p.image_url || (p.images && p.images[0]) || undefined}
          type="product"
        />
      )}
      {p && <SchemaLd type="product" ekey={p.slug || p.id} />}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full flex-1 pb-24 md:pb-8">
        <button onClick={() => navigate("/")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-steel mb-6">
          <ChevronLeft size={16} /> Back to catalog
        </button>
        {!p ? <div className="h-96 animate-pulse bg-slate-100 rounded-xl" /> : (
          <div className="grid md:grid-cols-2 gap-10">
            <ProductGallery
              images={p.images && p.images.length ? p.images : (p.image_url ? [p.image_url] : [])}
              name={p.name}
              alt={`${p.brand && !p.name.toLowerCase().startsWith(p.brand.toLowerCase()) ? p.brand + " " : ""}${p.name}${p.category ? " – " + p.category : ""} | ACP Online`}
            />
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{p.category}</div>
              <h1 className="mt-1 text-2xl sm:text-3xl font-extrabold break-words">{p.name}</h1>
              {p.short_description && <p className="mt-2 text-sm text-muted-foreground" data-testid="pdp-short-description">{p.short_description}</p>}
              <div className="mt-1 flex items-center gap-3 text-sm">
                <span className="font-mono text-muted-foreground">SKU {p.sku}</span>
                {p.mpn && <span className="font-mono text-muted-foreground" data-testid="pdp-mpn">MPN {p.mpn}</span>}
                {p.brand && (
                  <button data-testid="pdp-brand" onClick={() => navigate(`/brand/${encodeURIComponent(p.brand)}`)}
                    className="font-semibold text-steel hover:text-accent transition-colors">
                    Brand: {p.brand}
                  </button>
                )}
              </div>
              <div className="mt-4 flex items-center gap-3 flex-wrap">
                {p.poa ? (
                  <span className="text-2xl sm:text-3xl font-extrabold text-steel">{L.poa_price}</span>
                ) : (
                  <>
                    <span className="text-4xl font-extrabold font-mono text-accent">{money(p.price)}</span>
                    <span className="text-sm text-muted-foreground">incl. 15% GST</span>
                  </>
                )}
              </div>
              {!p.poa && p.on_special ? (
                <div className="mt-1 flex items-center gap-3" data-testid="pdp-saving">
                  <span className="text-lg line-through text-muted-foreground font-mono">{money(p.list_price)}</span>
                  <span className="text-sm font-bold text-red-600 uppercase tracking-wide">You save {money(p.saving)}</span>
                </div>
              ) : p.rrp ? (
                <div className="mt-1 flex items-center gap-3" data-testid="pdp-rrp-saving">
                  <span className="text-lg line-through text-muted-foreground font-mono">RRP {money(p.rrp)}</span>
                  <span className="text-sm font-bold text-red-600 uppercase tracking-wide">You save {money(p.rrp_saving)}</span>
                </div>
              ) : null}
              <div className="mt-3">
                {p.poa
                  ? <Badge className="bg-accent text-steel hover:bg-accent">Price on application — request a quote</Badge>
                  : p.availability?.status === "lead_time"
                  ? <Badge className="bg-amber-500 text-steel hover:bg-amber-500 whitespace-normal" data-testid="pdp-availability">Availability: Available to Order</Badge>
                  : p.availability?.status === "contact"
                  ? <Badge className="bg-slate-600 hover:bg-slate-600 whitespace-normal" data-testid="pdp-availability">Availability: Please Contact Us</Badge>
                  : p.stock > 0
                  ? <Badge className="bg-emerald-600 hover:bg-emerald-600 whitespace-normal" data-testid="pdp-availability">{L.pdp_in_stock.replace("{stock}", p.stock)}</Badge>
                  : <Badge className="bg-slate-600 hover:bg-slate-600 whitespace-normal" data-testid="pdp-availability">{L.pdp_out_of_stock}</Badge>}
                {p.availability?.lead_time && (
                  <p className="mt-2 text-sm text-steel" data-testid="pdp-lead-time">
                    <b>Estimated Supplier Lead Time:</b> {p.availability.lead_time}
                    <span className="block text-xs text-muted-foreground">Supplier estimate only — not a guaranteed delivery date. We'll confirm timing after you order.</span>
                  </p>)}
                {p.availability?.message && <p className="mt-1 text-sm text-muted-foreground" data-testid="pdp-availability-message">{p.availability.message}</p>}
              </div>

              {p.poa || p.availability?.action === "quote" ? (
                <Button data-testid="pdp-request-quote" onClick={() => setEnquiry(true)}
                  className="mt-6 min-h-12 h-auto py-2 w-full whitespace-normal leading-tight bg-accent text-steel hover:bg-amber-600 font-semibold">
                  <FileText size={18} className="mr-2 shrink-0" /> {L.request_quote}
                </Button>
              ) : p.available ? (
                <div className="mt-6 flex items-center gap-3">
                  <div className="flex items-center border rounded-md">
                    <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2 hover:bg-slate-100">−</button>
                    <span className="w-10 text-center" data-testid="pdp-qty">{qty}</span>
                    <button onClick={() => setQty(Math.min(p.max_qty ?? p.stock, qty + 1))} className="px-3 py-2 hover:bg-slate-100">+</button>
                  </div>
                  <Button data-testid="pdp-add-to-cart" onClick={() => { add(p, qty); toast.success("Added to cart"); }}
                    className="flex-1 min-h-12 h-auto py-2 whitespace-normal leading-tight bg-steel hover:bg-steel-light text-white font-semibold">
                    <ShoppingCart size={18} className="mr-2 shrink-0" /> {p.backorder ? "Order now (backorder)" : L.add_to_cart}
                  </Button>
                </div>
              ) : (
                <Button data-testid="pdp-enquire" onClick={() => setEnquiry(true)} variant="outline"
                  className="mt-6 min-h-12 h-auto py-2 w-full whitespace-normal leading-tight border-slate-400 font-semibold">
                  <Phone size={18} className="mr-2 shrink-0" /> {L.pdp_enquire_button}
                </Button>
              )}

              <div className="mt-8 grid gap-3 text-sm text-muted-foreground">
                <div className="flex items-center gap-2"><Truck size={16} className="text-accent" /> FREE NZ shipping on orders $1,000+</div>
                <div className="flex items-center gap-2"><ShieldCheck size={16} className="text-accent" /> All prices include 15% NZ GST</div>
              </div>
            </div>
          </div>
        )}

        {p && p.documents && p.documents.length > 0 && (
          <section className="mt-10 max-w-4xl" data-testid="pdp-documents">
            <h2 className="text-xl font-extrabold text-steel border-b-2 border-accent pb-2 mb-4">Downloads</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {p.documents.map((d, i) => {
                const url = typeof d === "string" ? d : d.url;
                const name = (typeof d === "string" ? url.split("/").pop() : d.name) || "Document";
                return (
                  <a key={i} href={url} target="_blank" rel="noreferrer" data-testid={`pdp-doc-${i}`}
                    className="flex items-center gap-3 rounded-lg border bg-white p-3 hover:border-accent transition-colors">
                    <FileText size={20} className="text-accent shrink-0" />
                    <span className="text-sm font-medium truncate">{name}</span>
                  </a>
                );
              })}
            </div>
          </section>
        )}

        {p && <ProductServices productId={p.id} />}

        {p && p.description && (
          <section className="mt-12 max-w-4xl" data-testid="pdp-specifications">
            <h2 className="text-xl font-extrabold text-steel border-b-2 border-accent pb-2 mb-5">Specifications</h2>
            <div
              className="prose prose-slate max-w-none prose-headings:font-bold prose-headings:text-steel prose-h3:text-base prose-h3:mt-5 prose-h3:mb-2 prose-p:text-muted-foreground prose-p:leading-relaxed prose-li:text-muted-foreground prose-strong:text-steel prose-ul:my-2"
              dangerouslySetInnerHTML={{ __html: cleanSpecs(p.description) }}
            />
          </section>
        )}
      </main>
      {p && <EnquiryDialog product={p} quote={p.poa || p.availability?.action === "quote"} open={enquiry} onOpenChange={setEnquiry} />}

      {/* Sticky mobile buy bar — keeps price + action one tap away */}
      {p && (
        <div data-testid="pdp-mobile-buybar"
          className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t shadow-[0_-4px_16px_rgba(0,0,0,0.08)] px-4 py-3 flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] text-muted-foreground truncate">{p.name}</div>
            {p.poa ? (
              <div className="text-base font-extrabold text-steel leading-tight">{L.poa_price}</div>
            ) : (
              <div className="text-lg font-extrabold font-mono text-accent leading-tight">
                {money(p.price)} <span className="text-[10px] font-sans text-muted-foreground">{L.incl_gst}</span>
              </div>
            )}
          </div>
          {p.poa || p.availability?.action === "quote" ? (
            <Button data-testid="pdp-buybar-quote" onClick={() => setEnquiry(true)}
              className="shrink-0 h-11 px-5 bg-accent text-steel hover:bg-amber-600 font-semibold">
              <FileText size={18} className="mr-2" /> Quote
            </Button>
          ) : p.available ? (
            <Button data-testid="pdp-buybar-add" onClick={() => { add(p, qty); toast.success("Added to cart"); }}
              className="shrink-0 h-11 px-4 max-w-[55%] whitespace-normal leading-tight bg-steel hover:bg-steel-light text-white font-semibold">
              <ShoppingCart size={18} className="mr-2 shrink-0" /> {L.add_to_cart}
            </Button>
          ) : (
            <Button data-testid="pdp-buybar-enquire" onClick={() => setEnquiry(true)}
              variant="outline" className="shrink-0 h-11 px-4 max-w-[55%] whitespace-normal leading-tight border-slate-400 font-semibold">
              <Phone size={18} className="mr-2 shrink-0" /> {L.enquire_button_mobile}
            </Button>
          )}
        </div>
      )}

      <Footer />
    </div>
  );
}

function ProductGallery({ images, name, alt }) {
  const [active, setActive] = useState(0);
  const [broken, setBroken] = useState({});
  const altText = alt || name;
  if (!images.length) {
    return (
      <div className="rounded-2xl overflow-hidden border bg-white aspect-square grid place-items-center bg-slate-100 text-slate-300">
        <Package size={72} strokeWidth={1.5} />
      </div>
    );
  }
  return (
    <div>
      <div className="rounded-2xl overflow-hidden border bg-white aspect-square" data-testid="pdp-gallery-main">
        {broken[active] ? (
          <div className="w-full h-full grid place-items-center bg-slate-100 text-slate-400 text-sm" data-testid="pdp-image-unavailable">
            <div className="text-center"><Package size={56} strokeWidth={1.5} className="mx-auto mb-2" />Image temporarily unavailable</div>
          </div>
        ) : (
        <img src={images[active]} alt={altText} title={altText} className="w-full h-full object-contain"
          onError={() => setBroken((b) => ({ ...b, [active]: true }))} />)}
      </div>
      {images.length > 1 && (
        <div className="mt-3 flex gap-2 flex-wrap" data-testid="pdp-gallery-thumbs">
          {images.map((img, i) => (
            <button key={i} data-testid={`pdp-thumb-${i}`} onClick={() => setActive(i)}
              className={`w-16 h-16 rounded-lg overflow-hidden border-2 bg-white ${active === i ? "border-accent" : "border-transparent hover:border-slate-300"}`}>
              <img src={img} alt={`${name} – view ${i + 1}`} title={`${name} – view ${i + 1}`} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
